<template>
    <div
        v-if="!removeRegions"
        :class="{ 'hidden': hideRegions }">
        <div
            ref="isoregionTooltip"
            class="margin-bottom-10"
            data-tooltiptarget=".entityTooltip"
            data-tooltipoptions="{'width':350,'hideArrow':false,'position':'right','showOverlay':true}">
            <p class="entity__headline">
                <label for="isoregion">
                    {{ trans('FORM_SEARCH_REGIONS') }}
                </label>
            </p>
            <slot
                v-if="!regionFieldDisabled"
                name="region"></slot>
            <slot name="isoregion"></slot>
        </div>
        <div
            ref="isocountryTooltip"
            data-tooltiptarget=".entityTooltip"
            data-tooltipoptions="{'width':350,'hideArrow':false,'position':'right','showOverlay':true}">
            <div
                v-if="showSearchField"
                class="margin-bottom-10">
                <p
                    id="isocountriesTitle"
                    class="entity__headline">
                    <label
                        for="isocountries"
                        class="required">{{ locale !== 'en' ? trans('ACCOUNT_FAST_REGISTRATION_COUNTRY') : trans('FORM_COUNTRIES_TERRITORIES') }}</label>
                </p>
                <select-searchable
                    id="isocountries"
                    :options="filteredCountries"
                    :disabled="allFiltersDisabled"
                    option-id-prefix="isocountry_"
                    name="isocountries[]"
                    :search-placeholder="trans('FORM_COUNTRY_SEARCH')"
                    search-name="isocountrySearch"
                    search-id="isocountrySearch"
                    search-input-labelled-by="isocountriesTitle"
                    @select="setSelectedCountry" />
            </div>
        </div>
    </div>
</template>

<script>
    import SelectSearchable from 'components/selectSearchable';
    import Translator from 'tools/trans';
    import { getJsonData } from 'tools/domHelpers';

    const isoRegionElementExists = document.getElementById('isoregion') !== null;

    export default {
        components: {
            'select-searchable': SelectSearchable,
        },
        model: {
            prop: 'checked',
            event: 'change',
        },
        props: {
            id: { type: String, required: true },
            disabled: { type: Boolean, required: false, default: false },
            blocked: { type: Boolean, required: false, default: false },
            locale: { type: String, required: false, default: 'en' },
        },
        data() {
            return {
                isoregions: getJsonData('#isoregion', 'allregions'),
                regionsWithCountriesDisabled: getJsonData('#isoregion', 'countriesdisabled'),
                regionFieldDisabled: false,
                removeRegions: false,
                hideRegions: false,
                showSearchField: false,
                countries: [],
                currentRegionSelected: 0,
                selectedCountries: [],
                selectedCountriesIDs: [],
            };
        },
        computed: {
            allFiltersDisabled: {
                get() {
                    return this.$store.state.allFiltersDisabled;
                },
                set(value) {
                    this.$store.commit('allFiltersDisabled', value);
                },
            },
            countryDisabled() {
                return this.regionsWithCountriesDisabled.indexOf(this.currentRegionSelected) > -1;
            },
            filteredCountries() {
                if (this.currentRegionSelected === 0) {
                    return this.countries;
                } else {
                    return this.countries.filter(country => {
                        return country.parent === this.currentRegionSelected;
                    });
                }
            },
        },
        watch: {
            disabled(value) {
                this.setDisabled(value);
            },
        },
        methods: {
            trans: Translator.trans,
            fireFilterChangeEvent() {
                const event = document.createEvent('HTMLEvents');
                event.initEvent('change', true, true);
                document.getElementById('sortMethod').dispatchEvent(event);
            },
            setSelectedRegion(e) {
                this.currentRegionSelected = parseInt(e.target.options[e.target.selectedIndex].value);
                if (this.currentRegionSelected !== 0) {
                    // don't clear selected countries if switched to "all, id=0"
                    this.selectedCountriesIDs = [];
                }
                this.updateRegionsHiddenFormData();
                this.setEnabledCountries();
            },
            setSelectedCountry(selectedCountries) {
                this.selectedCountries = selectedCountries;
                this.selectedCountriesIDs = [];
                for (let i = 0, len = this.selectedCountries.length; i < len; i++) {
                    if (this.selectedCountries[i].checked === true) {
                        this.selectedCountriesIDs.push(parseInt(this.selectedCountries[i].value));
                    }
                }
                this.updateRegionsHiddenFormData();
            },
            setEnabledCountries() {
                this.showSearchField = true;
                const enabledCountries = this.isoregions.filter(region => {
                    if (this.currentRegionSelected > 0) {
                        return this.regionIsCountry(region) && region.idParent === this.currentRegionSelected;
                    } else {
                        return this.regionIsCountry(region);
                    }
                });
                enabledCountries.sort(function(a, b) {
                    return a.displayName.localeCompare(b.displayName);
                });

                this.countries = [];
                this.countries = enabledCountries.map(country => {
                    return {
                        name: country.displayName,
                        value: parseInt(country.id),
                        checked: document.querySelector(`#region option[value="${ parseInt(country.id) }"]`).selected,
                        parent: country.idParent,
                    };
                });

                this.selectedCountries = this.countries.filter(country => country.checked);
                // disable countries if enabledCountries is empty
                if (enabledCountries.length < 1) {
                    this.showSearchField = false;
                }
            },
            updateRegionsHiddenFormData() {
                let regionsToSearch = [];
                const regionSelect = document.getElementById('region');

                //reset region select
                for (let i = 0, l = regionSelect.options.length; i < l; i++) {
                    regionSelect.options[i].selected = false;
                }

                //get selected regions from view selects
                if (this.selectedCountriesIDs.length > 0) {
                    regionsToSearch = this.selectedCountriesIDs;
                } else if (this.currentRegionSelected > 0) {
                    regionsToSearch.push(this.currentRegionSelected);
                }

                //update regions select
                if (regionsToSearch.length > 0) {
                    for (let i = 0, l = regionSelect.options.length, o; i < l; i++) {
                        o = regionSelect.options[i];
                        if (regionsToSearch.indexOf(parseInt(o.value)) !== -1) {
                            o.selected = true;
                        }
                    }
                } else {
                    regionSelect.options[0].selected = true;
                }
            },
            getRegionObjectById(selectedOptionId) {
                for (const i in this.isoregions) {
                    if (Object.prototype.hasOwnProperty.call(this.isoregions[i], 'id') &&
                        parseInt(this.isoregions[i].id) === parseInt(selectedOptionId)) {

                        return this.isoregions[i];
                    }
                }

                return null;
            },
            regionIsCountry(region) {
                return region.idParent !== null;
            },
            initData() {
                const regionSelect = document.getElementById('region');

                let countryIsSelected = false;
                if (!regionSelect.options) {
                    this.removeRegions = true;

                    return;
                }
                for (let i = 0; i < regionSelect.options.length; i++) {
                    const optionId = regionSelect.options[i].value;

                    const region = this.getRegionObjectById(optionId);
                    if (region === null) {
                        continue;
                    }

                    if (this.regionIsCountry(region)) {
                        this.countries.push({
                            name: region.displayName,
                            value: parseInt(region.id),
                            checked: regionSelect.options[i].selected,
                            parent: region.idParent,
                        });
                    }
                    if (regionSelect.options[i].selected) {
                        if (this.regionIsCountry(region)) {
                            this.selectedCountriesIDs.push(parseInt(region.id));
                            countryIsSelected = true;
                        } else {
                            this.currentRegionSelected = parseInt(optionId);
                        }
                    }
                }

                if (countryIsSelected) {
                    this.setEnabledCountries();
                    this.currentRegionSelected = this.selectedCountries.every(el => el.parent === this.selectedCountries[0].parent) ? this.selectedCountries[0].parent : 0;
                }
            },
            updateRegionsViewFromData() {
                const isoregionSelect = document.getElementById('isoregion');

                for (let i = 0, l = isoregionSelect.options.length; i < l; i++) {
                    const option = isoregionSelect.options[i];

                    if (parseInt(option.value) === this.currentRegionSelected) {
                        option.selected = true;
                        this.setEnabledCountries();
                    }
                }
            },
            updateCountriesViewFromData() {
                const countriesToSearch = this.selectedCountriesIDs;
                for (let i = 0, l = this.countries.length, checkbox; i < l; i++) {
                    checkbox = this.countries[i];
                    if (countriesToSearch.indexOf(checkbox.value) !== -1) {
                        checkbox.checked = true;
                    }
                }
            },
            bindIsoRegionUpdateEvent() {
                document.getElementById('isoregion').addEventListener('change', event => {
                    this.setSelectedRegion(event);
                });
            },
            setTooltip() {
                if (this.blocked) {
                    this.$refs.isoregionTooltip.dataset.tooltip = 'click';
                    this.$refs.isocountryTooltip.dataset.tooltip = 'click';
                }
            },
            updateHideRegion() {
                const BANNED_FILTER = ['infos', 'sources', 'companies', 'ode'];
                const ALLOWED_FILTER = ['statistics', 'forecasts', 'xmo', 'cmo', 'dmo', 'studies', 'branchreports', 'dossiers', 'toplists', 'surveys', 'topics', 'groupA', 'groupB', 'groupC'];
                const formCheckBoxes = document.querySelectorAll('[data-entityfilter] input[type=checkbox]');
                let bannedSelected = false;
                let allowedSelected = false;

                formCheckBoxes.forEach(checkBox => {
                    if (checkBox.checked && BANNED_FILTER.indexOf(checkBox.name) > -1) {
                        bannedSelected = true;
                    } else if (checkBox.checked && ALLOWED_FILTER.indexOf(checkBox.name) > -1) {
                        allowedSelected = true;
                    }
                });

                const hideRegions = bannedSelected === true && allowedSelected === false;
                this.regionFieldDisabled = hideRegions;
                this.hideRegions = hideRegions;
            },
            bindResetEvent() {
                document.querySelector('[data-entityfilter]').addEventListener('reset', () => {
                    this.unselectAllRegions();
                });
            },
            unselectAllRegions() {
                // reset hidden region input
                const region = document.getElementById('region').options;
                for (let i = 0; i < region.length; i++) {
                    region[i].selected = false;
                }
                // reset iso region field
                this.currentRegionSelected = 0;
                const isoregion = document.getElementById('isoregion').options;
                for (let i = 0; i < isoregion.length; i++) {
                    isoregion[i].selected = false;
                }
                // reset country search field
                document.getElementById('isocountrySearch').value = '';
                // reset filter tags
                this.setEnabledCountries();
            },
        },
        mounted() {
            if (isoRegionElementExists) {
                this.initData();
                this.updateRegionsViewFromData();
                this.updateCountriesViewFromData();
                this.bindIsoRegionUpdateEvent();
                this.setTooltip();
                this.updateHideRegion();
                this.bindResetEvent();
            }
        },
    };

</script>
